/**
 * Booking service — create, read, cancel bookings.
 *
 * Singleton pattern. Throws ServiceError on failure.
 * Uses pricing.ts for price calculations.
 * Checks vehicle_availability for date conflicts before booking.
 */
import { supabase } from '../supabase'
import type { Cols, EmbedMany, EmbedOne, WithEmbed } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { tripPriceForVehicle } from '../utils/pricing'
import { EARLY_ACCESS, TIERED_FEE_POLICY } from '../utils/constants'
import { resolveGuestServiceFeePct } from '../utils/tiered-fee'
import { rentalDaysBetween } from '../utils/dates'
import { haversineKm } from '../utils/booking-location'
import { RENTAL_LIMITS } from '../utils/constants'
import { promoService } from './promo.service'
import { cancellationService } from './cancellation.service'
import { Sentry, captureException } from '../utils/sentry'
import { formatMarketTransmissionText } from '../utils/transmission'
import { normalizeVehicleMinRentalHours } from '../utils/min-rental-duration'
import { startsTooSoonForHostApproval } from '../utils/host-approval-policy'
import {
  INDEPENDENCE_CAMPAIGN,
  calculateIndependenceCampaignDiscountCents,
  isIndependenceCampaignCode,
} from '../utils/pricing-core'
import type {
  CreateBookingInput,
  BookingDetail,
  BookingListItem,
  CancelBookingInput,
  BookingRole,
  BookingListFilter,
  VehicleUnavailableRange,
} from '../types/booking.types'
import type { TripPriceBreakdown } from '../utils/pricing'

// ── Constants ────────────────────────────────────────────────────────────────

const UPCOMING_STATUSES = [
  'pending_payment',
  'awaiting_host',
  'confirmed',
  'pending_pickup',
  'active',
  'pending_return',
] as const
const DEFAULT_DELIVERY_RADIUS_KM = 10

type GeoPoint = { lat: number; lng: number }

function parsePostGISPoint(raw: unknown): GeoPoint | null {
  if (!raw) return null

  const finitePoint = (lng: number, lat: number): GeoPoint | null => {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
    return { lng, lat }
  }

  const parseWkbHexPoint = (value: string): GeoPoint | null => {
    const hex = value.trim()
    if (!/^[0-9a-fA-F]+$/.test(hex) || hex.length % 2 !== 0 || hex.length < 42) return null

    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16)
    }

    const view = new DataView(bytes.buffer)
    const endianFlag = view.getUint8(0)
    if (endianFlag !== 0 && endianFlag !== 1) return null

    const littleEndian = endianFlag === 1
    const type = view.getUint32(1, littleEndian)
    const geometryType = type & 0xff
    const hasSrid = (type & 0x20000000) !== 0
    if (geometryType !== 1) return null

    const coordinateOffset = 5 + (hasSrid ? 4 : 0)
    if (bytes.length < coordinateOffset + 16) return null

    return finitePoint(
      view.getFloat64(coordinateOffset, littleEndian),
      view.getFloat64(coordinateOffset + 8, littleEndian),
    )
  }

  const parseWktPoint = (value: string): GeoPoint | null => {
    const match = value
      .trim()
      .match(/^(?:SRID=\d+;)?POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i)
    if (!match) return null
    return finitePoint(Number(match[1]), Number(match[2]))
  }

  try {
    if (typeof raw === 'string') {
      const fromWkb = parseWkbHexPoint(raw)
      if (fromWkb) return fromWkb

      const fromWkt = parseWktPoint(raw)
      if (fromWkt) return fromWkt
    }

    const geo = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (geo?.type === 'Point' && Array.isArray(geo.coordinates) && geo.coordinates.length === 2) {
      return finitePoint(Number(geo.coordinates[0]), Number(geo.coordinates[1]))
    }
  } catch {
    // Invalid geometry
  }
  return null
}

function isUsableCoordinate(
  lat: number | null | undefined,
  lng: number | null | undefined,
): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  return lat !== 0 || lng !== 0
}

function locationError(kind: 'missing' | 'pickup' | 'dropoff'): ServiceError {
  return new ServiceError({
    message:
      kind === 'missing'
        ? 'Vehicle host location is required for booking'
        : `${kind} location is outside the vehicle delivery radius`,
    code:
      kind === 'missing'
        ? 'VEHICLE_LOCATION_REQUIRED'
        : kind === 'pickup'
          ? 'PICKUP_OUT_OF_RADIUS'
          : 'DROPOFF_OUT_OF_RADIUS',
    statusCode: 422,
    userMessageKey:
      kind === 'missing' ? 'checkout.vehicle_location_missing' : 'checkout.location_out_of_radius',
  })
}

function bookingAvailabilityError(reason?: string | null): ServiceError {
  const blocked = reason === 'blocked'
  return new ServiceError({
    message: blocked
      ? 'Vehicle is blocked for these dates'
      : 'Vehicle is already booked for these dates',
    code: blocked ? 'DATES_BLOCKED' : 'DATES_UNAVAILABLE',
    statusCode: 409,
    userMessageKey: blocked ? 'checkout.dates_blocked' : 'checkout.dates_unavailable',
  })
}

function isRentalOverlapError(error: { code?: string; message?: string }): boolean {
  const message = error.message ?? ''
  return (
    error.code === '23P01' ||
    /rentals_no_vehicle_overlap|conflicting key value|exclusion constraint/i.test(message)
  )
}

function blockedReasonFromTrigger(error: {
  code?: string
  message?: string
}): 'blocked' | 'reserved' | null {
  if (error.code !== 'P0001') return null
  const message = error.message ?? ''
  if (/blocked/i.test(message)) return 'blocked'
  if (/reservation|reserved|already has/i.test(message)) return 'reserved'
  return null
}

function locationErrorFromTrigger(error: { code?: string; message?: string }): ServiceError | null {
  if (error.code !== 'P0001') return null
  const message = error.message ?? ''
  if (/vehicle_home_location_required/i.test(message)) return locationError('missing')
  if (/pickup_out_of_delivery_radius/i.test(message)) return locationError('pickup')
  if (/dropoff_out_of_delivery_radius/i.test(message)) return locationError('dropoff')
  return null
}

function vehicleMinRentalDurationError(minRentalHours: number): ServiceError {
  return new ServiceError({
    message: `Vehicle minimum rental duration is ${minRentalHours} hours`,
    code: 'VEHICLE_MIN_RENTAL_DURATION',
    statusCode: 422,
    userMessageKey: 'checkout.min_rental_duration',
    details: { minRentalHours },
  })
}

function vehicleMinRentalErrorFromTrigger(error: {
  code?: string
  message?: string
  details?: string
}): ServiceError | null {
  if (error.code !== 'P0001') return null
  if (!/vehicle_min_rental_duration/i.test(error.message ?? '')) return null
  const minRentalHours = Number(error.details)
  return vehicleMinRentalDurationError(Number.isFinite(minRentalHours) ? minRentalHours : 48)
}

// ── Row interfaces ───────────────────────────────────────────────────────────

/** The `vehicles(title, vehicle_images(...))` embed both selects below share. */
type BookingVehicleEmbed = WithEmbed<
  Cols<'vehicles', 'title'>,
  { vehicle_images: EmbedMany<'vehicle_images', 'url' | 'is_primary' | 'blurhash'> }
> | null

/** Shape returned by `DETAIL_SELECT`. */
type BookingDetailRow = WithEmbed<
  Cols<
    'rentals',
    | 'id'
    | 'booking_code'
    | 'status'
    | 'vehicle_id'
    | 'host_id'
    | 'guest_id'
    | 'start_at'
    | 'end_at'
    | 'duration_hours'
    | 'pickup_address'
    | 'pickup_location'
    | 'dropoff_address'
    | 'dropoff_location'
    | 'is_same_location'
    | 'insurance_model'
    | 'base_rate_cents'
    | 'subtotal_cents'
    | 'insurance_fee_cents'
    | 'discount_code'
    | 'platform_fee_cents'
    | 'payment_processing_fee_cents'
    | 'discount_cents'
    | 'tax_cents'
    | 'total_cents'
    | 'host_payout_cents'
    | 'security_deposit_cents'
    // El depósito PACTADO y el depósito RETENIDO no son lo mismo, y confundirlos
    // es mentirle a alguien sobre su propia plata: `security_deposit_cents` es
    // lo que el host pidió, `deposit_held_cents` es lo que de verdad salió de la
    // tarjeta, y `deposit_released_cents` lo que ya volvió. Un viaje en
    // `pending_payment` tiene lo primero y no lo segundo.
    | 'deposit_held_cents'
    | 'deposit_released_cents'
    // Y lo ADJUDICADO, que es la tercera columna y la que explica una
    // devolución parcial. Sin ella, a alguien que puso $500 y recibe $300 la
    // pantalla le muestra "Depósito devuelto $300" sin decir dónde están los
    // otros $200 — que es la peor forma de contar una resta.
    | 'deposit_claimed_cents'
    | 'cancelled_at'
    | 'cancelled_by'
    | 'cancellation_reason'
    | 'host_response_deadline_at'
    | 'rental_terms_version'
    | 'host_responded_at'
    | 'created_at'
    | 'updated_at'
  >,
  {
    vehicles: BookingVehicleEmbed
    host: EmbedOne<'profiles', 'display_name' | 'avatar_url'>
    guest: EmbedOne<'profiles', 'display_name' | 'avatar_url'>
  }
>

/** Shape returned by `LIST_SELECT`. */
type BookingListRow = WithEmbed<
  Cols<
    'rentals',
    | 'id'
    | 'booking_code'
    | 'status'
    | 'vehicle_id'
    | 'start_at'
    | 'end_at'
    | 'total_cents'
    | 'created_at'
  >,
  { vehicles: BookingVehicleEmbed }
>

// ── Select queries ───────────────────────────────────────────────────────────

// PostgREST rejects `.select(...)` strings that contain literal newlines
// (it URL-encodes them as `%0A` and occasionally truncates mid-parse →
// 400). Keep these single-line. Same fix we applied to messaging.service.ts.
const DETAIL_SELECT =
  'id,booking_code,status,host_response_deadline_at,host_responded_at,rental_terms_version,vehicle_id,host_id,guest_id,start_at,end_at,duration_hours,pickup_address,pickup_location,dropoff_address,dropoff_location,is_same_location,insurance_model,base_rate_cents,subtotal_cents,insurance_fee_cents,discount_code,platform_fee_cents,payment_processing_fee_cents,discount_cents,tax_cents,total_cents,host_payout_cents,security_deposit_cents,deposit_held_cents,deposit_released_cents,deposit_claimed_cents,cancelled_at,cancelled_by,cancellation_reason,created_at,updated_at,vehicles!rentals_vehicle_id_fkey(title,vehicle_images(url,is_primary,blurhash)),host:profiles!rentals_host_id_fkey(display_name,avatar_url),guest:profiles!rentals_guest_id_fkey(display_name,avatar_url)'

const LIST_SELECT =
  'id,booking_code,status,vehicle_id,start_at,end_at,total_cents,created_at,vehicles!rentals_vehicle_id_fkey(title,vehicle_images(url,is_primary,blurhash))'

// ── Mappers ──────────────────────────────────────────────────────────────────

function getPrimaryImage(
  images: Array<{ url: string; is_primary: boolean; blurhash: string | null }> | null | undefined,
): { url: string; blurhash: string | null } | null {
  if (!images || images.length === 0) return null
  const primary = images.find((img) => img.is_primary) ?? images[0]
  return primary ? { url: primary.url, blurhash: primary.blurhash ?? null } : null
}

function mapBookingDetail(row: BookingDetailRow): BookingDetail {
  const pickupLocation = parsePostGISPoint(row.pickup_location)
  const dropoffLocation = parsePostGISPoint(row.dropoff_location)
  const pricing: TripPriceBreakdown = {
    subtotalCents: row.subtotal_cents,
    insuranceCents: row.insurance_fee_cents,
    guestServiceFeeCents: (row.platform_fee_cents ?? 0) + (row.payment_processing_fee_cents ?? 0),
    platformFeeProcessingCents: row.payment_processing_fee_cents ?? 0,
    promoDiscountCents: row.discount_cents,
    taxCents: row.tax_cents,
    depositCents: row.security_deposit_cents ?? 0,
    totalCents: row.total_cents,
    hostEarningsCents: row.host_payout_cents,
    platformFeeCents: row.platform_fee_cents,
  }

  return {
    id: row.id,
    bookingCode: row.booking_code,
    status: row.status,
    hostResponseDeadlineAt: row.host_response_deadline_at ?? null,
    hostRespondedAt: row.host_responded_at ?? null,
    rentalTermsVersion: row.rental_terms_version ?? null,
    vehicleId: row.vehicle_id,
    vehicleTitle: formatMarketTransmissionText(row.vehicles?.title),
    vehiclePrimaryImageUrl: getPrimaryImage(row.vehicles?.vehicle_images)?.url ?? null,
    vehiclePrimaryImageBlurhash: getPrimaryImage(row.vehicles?.vehicle_images)?.blurhash ?? null,
    hostId: row.host_id,
    hostDisplayName: row.host?.display_name ?? null,
    hostAvatarUrl: row.host?.avatar_url ?? null,
    guestId: row.guest_id,
    guestDisplayName: row.guest?.display_name ?? null,
    guestAvatarUrl: row.guest?.avatar_url ?? null,
    startAt: row.start_at,
    endAt: row.end_at,
    durationHours: row.duration_hours,
    pickupAddress: row.pickup_address,
    pickupLat: pickupLocation?.lat ?? null,
    pickupLng: pickupLocation?.lng ?? null,
    dropoffAddress: row.dropoff_address,
    dropoffLat: dropoffLocation?.lat ?? null,
    dropoffLng: dropoffLocation?.lng ?? null,
    isSameLocation: row.is_same_location,
    insuranceModel: row.insurance_model,
    discountCode: row.discount_code,
    pricing,
    depositHeldCents: row.deposit_held_cents ?? 0,
    depositReleasedCents: row.deposit_released_cents ?? 0,
    depositClaimedCents: row.deposit_claimed_cents ?? 0,
    totalCents: row.total_cents,
    hostPayoutCents: row.host_payout_cents,
    cancelledAt: row.cancelled_at,
    cancelledBy: row.cancelled_by,
    cancellationReason: row.cancellation_reason,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

function mapBookingListItem(row: BookingListRow): BookingListItem {
  return {
    id: row.id,
    bookingCode: row.booking_code,
    status: row.status,
    vehicleId: row.vehicle_id,
    vehicleTitle: formatMarketTransmissionText(row.vehicles?.title),
    vehiclePrimaryImageUrl: getPrimaryImage(row.vehicles?.vehicle_images)?.url ?? null,
    vehiclePrimaryImageBlurhash: getPrimaryImage(row.vehicles?.vehicle_images)?.blurhash ?? null,
    startAt: row.start_at,
    endAt: row.end_at,
    totalCents: row.total_cents,
    createdAt: row.created_at,
  }
}

// ── Service ──────────────────────────────────────────────────────────────────

class BookingServiceImpl {
  /**
   * Date ranges that the UI calendar must not allow selecting.
   *
   * Runs through a SECURITY DEFINER RPC because normal rental RLS hides other
   * guests' bookings. The RPC returns only blocked ranges, never guest/payment
   * details.
   */
  async getVehicleUnavailableRanges(input: {
    vehicleId: string
    fromDate: string
    toDate: string
  }): Promise<VehicleUnavailableRange[]> {
    const { data, error } = await supabase.rpc('get_vehicle_unavailable_ranges', {
      p_vehicle_id: input.vehicleId,
      p_from_date: input.fromDate,
      p_to_date: input.toDate,
      p_timezone: 'America/El_Salvador',
    })

    if (error) throw ServiceError.fromSupabaseError(error)

    return (data ?? []).map((row) => ({
      startDate: row.start_date,
      endDate: row.end_date,
      reason: row.reason,
      sourceId: row.source_id,
      freeFrom: row.free_from ?? null,
      freeUntil: row.free_until ?? null,
    }))
  }

  async getCompletedPairRentals(guestId: string, hostId: string): Promise<number> {
    const { data, error } = await supabase.rpc('completed_pair_rentals', {
      p_guest_id: guestId,
      p_host_id: hostId,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    const count = typeof data === 'number' ? data : Number(data)
    return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0
  }

  /**
   * Create a booking after validating availability and calculating price.
   */
  async createBooking(guestId: string, input: CreateBookingInput): Promise<BookingDetail> {
    // Set Sentry context so any error in this flow includes booking details
    Sentry.setContext('booking', {
      vehicleId: input.vehicleId,
      guestId,
      startDate: input.startDate,
      endDate: input.endDate,
      insuranceTier: input.insuranceTier,
      promoCode: input.promoCode ?? null,
    })

    // 1) Fetch vehicle details for pricing
    const { data: vehicle, error: vehicleError } = await supabase
      .from('vehicles')
      .select(
        'id, host_id, organization_id, coverage_path, daily_rate_cents, security_deposit_cents, coverage_daily_rate_cents, coverage_review_status, status, min_rental_hours, home_location, home_address, delivery_radius_km',
      )
      .eq('id', input.vehicleId)
      .is('deleted_at', null)
      .single()

    if (vehicleError) throw ServiceError.fromSupabaseError(vehicleError)
    if (!vehicle) throw ServiceError.notFound('Vehicle')
    if (vehicle.status !== 'active') {
      throw ServiceError.validation('Vehicle is not available for booking', {
        vehicleStatus: vehicle.status,
      })
    }
    if (vehicle.host_id === guestId) {
      throw ServiceError.validation('You cannot book your own vehicle')
    }

    // DESACOPLE DE LA PRIMA (P3): el MISMO predicado que usa el cargo
    // autoritativo y la trigger. Solo un vehículo persona-natural con cobertura
    // de plataforma aceptada lleva prima; own_policy/exempt/organización,
    // prima cero.
    const hasPlatformCoverage =
      vehicle.organization_id == null &&
      vehicle.coverage_path === 'platform' &&
      vehicle.coverage_review_status === 'accepted'

    const homeLocation = parsePostGISPoint(vehicle.home_location)
    const homeAddress = vehicle.home_address?.trim()
    if (!homeLocation || !homeAddress) throw locationError('missing')

    const deliveryRadiusKm =
      typeof vehicle.delivery_radius_km === 'number' && Number.isFinite(vehicle.delivery_radius_km)
        ? Math.max(0, vehicle.delivery_radius_km)
        : DEFAULT_DELIVERY_RADIUS_KM

    const hasPickup =
      isUsableCoordinate(input.pickupLat, input.pickupLng) && input.pickupAddress.trim().length > 0
    const pickupLocation = hasPickup
      ? { lat: input.pickupLat, lng: input.pickupLng, address: input.pickupAddress.trim() }
      : { lat: homeLocation.lat, lng: homeLocation.lng, address: homeAddress }

    const hasDropoff =
      isUsableCoordinate(input.dropoffLat, input.dropoffLng) &&
      input.dropoffAddress.trim().length > 0
    const dropoffLocation = hasDropoff
      ? { lat: input.dropoffLat, lng: input.dropoffLng, address: input.dropoffAddress.trim() }
      : { lat: pickupLocation.lat, lng: pickupLocation.lng, address: pickupLocation.address }

    if (haversineKm(homeLocation, pickupLocation) > deliveryRadiusKm) {
      throw locationError('pickup')
    }
    if (haversineKm(homeLocation, dropoffLocation) > deliveryRadiusKm) {
      throw locationError('dropoff')
    }

    // Defense-in-depth: even though vehicles_select_active RLS hides blocked
    // hosts' vehicles (so reaching this point with one is unlikely), check
    // the symmetric block via the same SQL helper. This stops bookings
    // initiated through stale caches, deep-link bypass attempts, or future
    // code paths that don't go through the blocked-aware listing endpoints.
    const { data: blocked, error: blockError } = await supabase.rpc('is_user_blocked', {
      viewer_id: guestId,
      target_id: vehicle.host_id,
    })
    if (blockError) throw ServiceError.fromSupabaseError(blockError)
    if (blocked) {
      throw new ServiceError({
        message: 'You cannot book this vehicle.',
        code: 'BOOKING_BLOCKED',
        statusCode: 403,
      })
    }

    // 2) Validate dates before idempotency reuse. Otherwise an old
    // pending_payment rental could bypass a newly configured vehicle minimum.
    const startDate = new Date(input.startDate)
    const endDate = new Date(input.endDate)
    if (startsTooSoonForHostApproval(input.startDate)) {
      throw new ServiceError({
        message: 'Choose a pickup time at least three hours from now.',
        code: 'BOOKING_TOO_SOON',
        statusCode: 422,
      })
    }
    const days = rentalDaysBetween(startDate, endDate)
    const durationHours = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60))
    const vehicleMinRentalHours = normalizeVehicleMinRentalHours(vehicle.min_rental_hours)
    const minimumRentalHours = vehicleMinRentalHours ?? RENTAL_LIMITS.minHours

    if (durationHours < minimumRentalHours) {
      if (vehicleMinRentalHours) throw vehicleMinRentalDurationError(vehicleMinRentalHours)
      throw ServiceError.validation(`Minimum rental duration is ${RENTAL_LIMITS.minHours} hours`)
    }
    if (days > RENTAL_LIMITS.maxDays) {
      throw ServiceError.validation(`Maximum rental duration is ${RENTAL_LIMITS.maxDays} days`)
    }

    // ── Find-or-create guard ─────────────────────────────────────────────────
    // If the caller already has a pending_payment rental for this exact
    // vehicle + date window, reuse it. Prevents the "pagar ahora always
    // errors" retry loop where a previous failed attempt leaves an orphan
    // pending_payment rental that then blocks every subsequent booking
    // attempt via the availability check (which treats pending_payment as
    // upcoming).
    const requestedPromoCode = input.promoCode?.trim().toUpperCase() ?? null

    {
      const { data: existingRental, error: existingError } = await supabase
        .from('rentals')
        .select(DETAIL_SELECT)
        .eq('guest_id', guestId)
        .eq('vehicle_id', input.vehicleId)
        .eq('status', 'pending_payment')
        .eq('start_at', input.startDate)
        .eq('end_at', input.endDate)
        .limit(1)
        .maybeSingle()

      if (existingError) throw ServiceError.fromSupabaseError(existingError)
      if (existingRental) {
        const existing: BookingDetailRow = existingRental
        const sameCheckoutChoices =
          (existing.discount_code ?? null) === requestedPromoCode &&
          existing.pickup_address.trim() === pickupLocation.address &&
          existing.dropoff_address.trim() === dropoffLocation.address &&
          existing.is_same_location ===
            (input.isSameLocation ||
              (pickupLocation.address === dropoffLocation.address &&
                pickupLocation.lat === dropoffLocation.lat &&
                pickupLocation.lng === dropoffLocation.lng))

        if (sameCheckoutChoices) {
          return mapBookingDetail(existing)
        }
      }
    }

    // One unpaid hold per guest+vehicle. Anything still pending_payment
    // here is a leftover from a previous tap or a failed charge; leaving
    // it is how the overlap trigger and the calendar tell this guest
    // their own checkout is "already reserved".
    const { error: cancelOwnHoldsError } = await supabase
      .from('rentals')
      .update({
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancelled_by: 'system',
        cancellation_reason: 'Replaced unpaid checkout hold',
      })
      .eq('guest_id', guestId)
      .eq('vehicle_id', input.vehicleId)
      .eq('status', 'pending_payment')

    if (cancelOwnHoldsError) throw ServiceError.fromSupabaseError(cancelOwnHoldsError)

    // 3) Check availability (no overlapping bookings or blocked dates).
    // This must run through a SECURITY DEFINER RPC: client RLS can hide other
    // guests' rentals, which makes a client-side overlap query falsely pass.
    // The RPC also ignores this guest's own pending_payment rows so a
    // leftover hold cannot 409 the retry.
    const { data: availabilityRows, error: availabilityError } = await supabase.rpc(
      'check_vehicle_booking_availability',
      {
        p_vehicle_id: input.vehicleId,
        p_start_at: input.startDate,
        p_end_at: input.endDate,
        p_timezone: 'America/El_Salvador',
      },
    )

    if (availabilityError) throw ServiceError.fromSupabaseError(availabilityError)

    const availability = Array.isArray(availabilityRows) ? availabilityRows[0] : availabilityRows
    if (!availability?.is_available) {
      throw bookingAvailabilityError(availability?.reason)
    }

    // 4) Validate promo code (if provided), otherwise apply the September
    // Independence OTA campaign when the selected rental touches Sep 15.
    const tripDays = Math.max(days, 1)
    const baseSubtotal = vehicle.daily_rate_cents * tripDays
    let promoDiscountCents = 0
    let promoCodeId: string | undefined
    let effectiveDiscountCode: string | null = null

    const effectiveInsuranceTier = EARLY_ACCESS.platformInsuranceEnabled
      ? input.insuranceTier
      : 'platform'

    // Host-set refundable deposit (0 when the host didn't enable one). Added to
    // the charged total, excluded from tax, and its Wompi fee is absorbed by the
    // host (deducted from host_payout via hostEarningsCents below).
    const depositCents = Math.max(0, vehicle.security_deposit_cents ?? 0)
    const completedPairTrips = await this.getCompletedPairRentals(guestId, vehicle.host_id)
    const guestServiceFeePct = resolveGuestServiceFeePct(completedPairTrips, TIERED_FEE_POLICY)

    const preDiscountPricing = tripPriceForVehicle(
      {
        dailyRateCents: vehicle.daily_rate_cents,
        securityDepositCents: vehicle.security_deposit_cents,
        coverageDailyRateCents: vehicle.coverage_daily_rate_cents,
        hasPlatformCoverage,
      },
      {
        days: tripDays,
        insuranceTier: effectiveInsuranceTier,
        promoDiscountCents: 0,
        guestServiceFeePct,
      },
    )

    const independenceDiscountCents = calculateIndependenceCampaignDiscountCents({
      startAt: input.startDate,
      endAt: input.endDate,
      serviceFeeCents: preDiscountPricing.platformFeeCents,
    })

    if (input.promoCode && !isIndependenceCampaignCode(input.promoCode)) {
      const promo = await promoService.validatePromoCode(input.promoCode, guestId, baseSubtotal)
      if (promo.valid && promo.discountCents) {
        promoDiscountCents = promo.discountCents
        promoCodeId = promo.promoCodeId
        effectiveDiscountCode = requestedPromoCode
      }
      // Silent if invalid — checkout already validated; race condition guard only
    } else if (independenceDiscountCents > 0) {
      promoDiscountCents = independenceDiscountCents
      effectiveDiscountCode = INDEPENDENCE_CAMPAIGN.code
    }

    // 5) Calculate pricing
    const pricing = tripPriceForVehicle(
      {
        dailyRateCents: vehicle.daily_rate_cents,
        securityDepositCents: vehicle.security_deposit_cents,
        coverageDailyRateCents: vehicle.coverage_daily_rate_cents,
        hasPlatformCoverage,
      },
      {
        days: tripDays,
        insuranceTier: effectiveInsuranceTier,
        promoDiscountCents,
        guestServiceFeePct,
      },
    )

    // 7) Generate booking code
    const bookingCode = `TG-${Date.now().toString(36).toUpperCase()}`

    // Insurance-model audit trail. Previously this was
    // hardcoded to 'platform' regardless of which insurance actually
    // covered the trip. The audit trail recorded the wrong policy, which
    // would cause damage-claim disputes to look up the wrong coverage.
    //
    // The rental starts as 'fleet' (the vehicle's listing model) and the
    // coverage issuance flips it to 'platform' only when the policy is
    // actually issued (coverage.ts L207-214). A booking that never issues
    // keeps the listing-level model, which is the honest record.
    const insuranceModel = 'fleet' as const

    // 8) Insert rental
    const { data: rental, error: insertError } = await supabase
      .from('rentals')
      .insert({
        vehicle_id: input.vehicleId,
        organization_id: vehicle.organization_id,
        guest_id: guestId,
        host_id: vehicle.host_id,
        booking_code: bookingCode,
        status: 'pending_payment',
        start_at: input.startDate,
        end_at: input.endDate,
        duration_hours: durationHours,
        pickup_address: pickupLocation.address,
        pickup_location: `POINT(${pickupLocation.lng} ${pickupLocation.lat})`,
        dropoff_address: dropoffLocation.address,
        dropoff_location: `POINT(${dropoffLocation.lng} ${dropoffLocation.lat})`,
        is_same_location:
          input.isSameLocation ||
          (pickupLocation.address === dropoffLocation.address &&
            pickupLocation.lat === dropoffLocation.lat &&
            pickupLocation.lng === dropoffLocation.lng),
        insurance_model: insuranceModel,
        base_rate_cents: vehicle.daily_rate_cents,
        subtotal_cents: pricing.subtotalCents,
        insurance_fee_cents: pricing.insuranceCents,
        discount_code: promoDiscountCents > 0 ? effectiveDiscountCode : null,
        platform_fee_cents: pricing.platformFeeCents,
        payment_processing_fee_cents: pricing.platformFeeProcessingCents,
        discount_cents: pricing.promoDiscountCents,
        tax_cents: pricing.taxCents,
        total_cents: pricing.totalCents,
        host_payout_cents: pricing.hostEarningsCents,
        // Host-set refundable deposit copied from the vehicle. The guest is
        // charged it inside total_cents; it becomes a real hold only at capture
        // (the RPC sets deposit_held_cents + opens a deposit_holds row). At
        // pending_payment nothing is held yet, so deposit_held_cents stays 0.
        security_deposit_cents: depositCents,
        timezone: 'America/El_Salvador',
      })
      .select(DETAIL_SELECT)
      .single()

    if (insertError) {
      if (insertError.hint === 'host_terms_acceptance_required') {
        throw new ServiceError({
          message: 'Host must accept the current terms before receiving new bookings',
          code: 'HOST_TERMS_NOT_CURRENT',
          statusCode: 409,
          userMessageKey: 'rental_terms_flow.host_unavailable',
        })
      }
      if (insertError.hint === 'guest_terms_acceptance_required') {
        throw new ServiceError({
          message: 'Guest must accept the current terms before booking',
          code: 'RENTAL_TERMS_UNAVAILABLE',
          statusCode: 409,
          userMessageKey: 'rental_terms_flow.changed',
        })
      }
      if (isRentalOverlapError(insertError)) {
        throw bookingAvailabilityError('reserved')
      }

      const triggerReason = blockedReasonFromTrigger(insertError)
      if (triggerReason) {
        throw bookingAvailabilityError(triggerReason === 'blocked' ? 'blocked' : 'reserved')
      }

      const triggerLocationError = locationErrorFromTrigger(insertError)
      if (triggerLocationError) throw triggerLocationError

      const triggerMinRentalError = vehicleMinRentalErrorFromTrigger(insertError)
      if (triggerMinRentalError) throw triggerMinRentalError

      throw ServiceError.fromSupabaseError(insertError)
    }

    // 9) Record promo code usage. This is intentionally blocking: a promo
    // redemption failure must not leave a discounted pending_payment rental
    // that the guest can still pay. Supabase doesn't give us a cross-table
    // transaction from the client, so the compensating action cancels the
    // just-created rental before surfacing the promo error.
    if (promoCodeId && promoDiscountCents > 0) {
      try {
        await promoService.recordUsage(promoCodeId, guestId, rental.id, promoDiscountCents)
      } catch (err) {
        const { error: cancelError } = await supabase
          .from('rentals')
          .update({
            status: 'cancelled',
            cancelled_at: new Date().toISOString(),
            cancelled_by: 'system',
            cancellation_reason: 'Promo redemption failed',
            discount_cents: 0,
          })
          .eq('id', rental.id)
          .eq('status', 'pending_payment')

        if (cancelError) {
          captureException(cancelError, {
            rentalId: rental.id,
            promoCodeId,
            guestId,
          })
        }
        throw err
      }
    }

    return mapBookingDetail(rental)
  }

  /**
   * Get full booking detail with vehicle, host, and guest info.
   */
  async getBookingDetail(bookingId: string): Promise<BookingDetail> {
    const { data, error } = await supabase
      .from('rentals')
      .select(DETAIL_SELECT)
      .eq('id', bookingId)
      .maybeSingle()

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) throw ServiceError.notFound('Booking')
    return mapBookingDetail(data)
  }

  /**
   * List bookings for a user, filtered by role (guest/host) and time.
   */
  async getUserBookings(
    userId: string,
    role: BookingRole,
    filter: BookingListFilter = 'all',
  ): Promise<BookingListItem[]> {
    let query = supabase
      .from('rentals')
      .select(LIST_SELECT)
      .order('created_at', { ascending: false })

    // Filter by role
    if (role === 'guest') {
      query = query.eq('guest_id', userId)
    } else {
      query = query.eq('host_id', userId)
    }

    // Filter by time
    if (filter === 'upcoming') {
      query = query.in('status', [...UPCOMING_STATUSES])
    } else if (filter === 'past') {
      query = query.in('status', ['completed', 'cancelled', 'disputed'])
    }

    const { data, error } = await query

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapBookingListItem)
  }

  /**
   * Cancel a booking with reason. Applies cancellation policy based on timing.
   */
  async cancelBooking(userId: string, input: CancelBookingInput): Promise<BookingDetail> {
    Sentry.setContext('booking_cancel', {
      bookingId: input.bookingId,
      userId,
      reason: input.reason,
    })

    await cancellationService.cancelBooking(userId, input.bookingId, input.reason)
    return this.getBookingDetail(input.bookingId)
  }

  /**
   * Free calendar dates after a charge that never completed.
   * RPC first (failed payment, no live 3DS). If a pending provider
   * attempt still exists, the edge function voids it then releases.
   */
  async releaseUnpaidHold(rentalId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('release_unpaid_rental_hold', {
      p_rental_id: rentalId,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    if (data === true) return true

    const { data: abandon, error: abandonError } = await supabase.functions.invoke(
      'release-payment-hold',
      { body: { rentalId } },
    )
    if (abandonError) {
      throw new ServiceError({
        message: abandonError.message,
        code: 'RELEASE_HOLD_FAILED',
        statusCode: 502,
      })
    }
    return (abandon as { released?: boolean } | null)?.released === true
  }
}

export const bookingService = new BookingServiceImpl()
